import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy with automatic retry + page reload on chunk load failure.
 * After a new deployment, old chunk hashes no longer exist on the server.
 * This catches the "Failed to fetch dynamically imported module" error,
 * reloads the page once to get the new HTML with updated chunk references.
 *
 * Uses sessionStorage to prevent infinite reload loops.
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  chunkName?: string
) {
  return lazy(() =>
    factory().catch((error: Error) => {
      const key = `chunk-retry-${chunkName ?? "global"}`;
      const hasRetried = sessionStorage.getItem(key);

      if (!hasRetried) {
        sessionStorage.setItem(key, "1");
        // Reload page to get fresh HTML with new chunk references
        window.location.reload();
        // Return a never-resolving promise to prevent React error boundary
        // from flashing before the reload completes
        return new Promise<{ default: T }>(() => {});
      }

      // Already retried once — clear flag and throw so error boundary catches it
      sessionStorage.removeItem(key);
      throw error;
    })
  );
}
